import { FC } from 'react';

import { StatusBadge } from '@/components/atoms/StatusBadge';
import { VehicleClearanceEntity } from '@/types';

export interface ClearanceTableProps {
  data: VehicleClearanceEntity[];
  onRowClick: (vin: string) => void;
  isLoading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
}

export const VehicleClearanceTable: FC<ClearanceTableProps> = ({
  data,
  onRowClick,
  isLoading = false,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-white-40">
        Loading clearance data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-white-40">
        No vehicle clearances found. Adjust your filters to see results.
      </div>
    );
  }

  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);
  const hasNextPage = page * pageSize < totalCount;

  return (
    <div className="bg-dark-green-lighter flex h-full w-full flex-col overflow-hidden rounded-lg border border-black-20">
      <div className="flex-1 overflow-auto">
        <table className="w-full table-fixed text-left text-sm text-white">
          <thead className="sticky top-0 z-10 border-b border-black-20 bg-[#2B4247] shadow-sm">
            <tr>
              <th className="w-64 px-6 py-4 font-medium text-white-80">VIN</th>
              <th className="w-48 px-6 py-4 font-medium text-white-80">OEM</th>
              <th className="w-32 px-6 py-4 font-medium text-white-80">Status</th>
              <th className="w-48 px-6 py-4 font-medium text-white-80">Inserted At</th>
              <th className="w-48 px-6 py-4 font-medium text-white-80">State Changed At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black-20">
            {data.map((row) => (
              <tr
                key={row.clearanceId}
                onClick={() => onRowClick(row.vin)}
                className="group cursor-pointer transition-colors hover:bg-white/5"
              >
                <td className="px-6 py-4 font-mono text-white-80 group-hover:text-white">
                  {row.vin}
                </td>
                <td className="w-48 px-6 py-4">{row.oem}</td>
                <td className="w-32 px-6 py-4">
                  <StatusBadge status={row.status} />
                </td>
                <td className="w-48 px-6 py-4 text-white-60">{row.insertedAt}</td>
                <td className="w-48 px-6 py-4 text-white-60">{row.stateChangedAt || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-dark-green flex items-center justify-between border-t border-black-20 p-4 text-xs text-white-60">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="cursor-pointer border-b border-white-40 bg-transparent pb-1 text-white outline-none"
          >
            <option className="bg-dark-green text-white" value={50}>
              50
            </option>
            <option className="bg-dark-green text-white" value={100}>
              100
            </option>
            <option className="bg-dark-green text-white" value={200}>
              200
            </option>
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span>
            Showing {startItem}-{endItem} of {totalCount}
          </span>
          <span className="h-4 w-[1px] bg-white-20"></span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-30"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNextPage}
              className="transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-30"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
